import { NavigationCallback } from "../types";

/**
 * Navigation observer for tracking page navigation events.
 */
class NavigationObserver {
  private currentPath: string;

  private historyListenersAreSet: boolean = false;
  private unloadListenersAreSet: boolean = false;

  private pageViewCallback: NavigationCallback | null = null;
  private pageLeaveCallback: NavigationCallback | null = null;

  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;

  /**
   * Creates a new NavigationObserver instance.
   */
  public constructor() {
    this.currentPath = window.location.pathname;
  }

  /**
   * Creates observer specifically for page view events. Also sends pageView event for initial load.
   * @param callback - Function to call when entering a new page
   * @returns void
   */
  public createPageViewObserver = (callback: NavigationCallback): void => {
    this.pageViewCallback = callback;
    this.setupListeners();

    this.handlePageView();
  };

  /**
   * Creates observer specifically for page leave events.
   * @param callback - Function to call when leaving the current page
   * @returns void
   */
  public createPageLeaveObserver = (callback: NavigationCallback): void => {
    this.pageLeaveCallback = callback;
    this.setupListeners();
  };

  /**
   * Destroys page view observer.
   * @returns void
   */
  public destroyPageViewObserver = (): void => {
    this.pageViewCallback = null;
    this.cleanupListeners();
  };

  /**
   * Destroys page leave observer.
   * @returns void
   */
  public destroyPageLeaveObserver = (): void => {
    this.pageLeaveCallback = null;
    this.cleanupListeners();
  };

  /**
   * Sets up observers if any are needed and not already active.
   * @returns void
   */
  private setupListeners = (): void => {
    if (this.historyListenersAreSet === false) {
      this.setupHistoryListeners();
      this.historyListenersAreSet = true;
    }

    if (this.unloadListenersAreSet === false) {
      this.setupUnloadListeners();
      this.unloadListenersAreSet = true;
    }
  };

  /**
   * Cleans up observers if none are needed.
   * @returns void
   */
  private cleanupListeners = (): void => {
    if (this.historyListenersAreSet === true) {
      this.cleanupHistoryListeners();
      this.historyListenersAreSet = false;
    }

    if (this.unloadListenersAreSet === true) {
      this.cleanupUnloadListeners();
      this.unloadListenersAreSet = false;
    }
  };

  /**
   * Sets up listeners for page load events.
   * @returns void
   */
  private setupHistoryListeners = (): void => {
    window.addEventListener("popstate", this.handleNavigation);
    this.overrideHistoryMethods();
  };

  /**
   * Removes history-related event listeners.
   * @returns void
   */
  private cleanupHistoryListeners = (): void => {
    window.removeEventListener("popstate", this.handleNavigation);
    this.restoreHistoryMethods();
  };

  /**
   * Sets up listeners for page unload events.
   * @returns void
   */
  private setupUnloadListeners = (): void => {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
  };

  /**
   * Removes unload-related event listeners.
   * @returns void
   */
  private cleanupUnloadListeners = (): void => {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
  };

  /**
   * Handles navigation events by checking if the path has changed.
   * @returns void
   */
  private handleNavigation = (): void => {
    const newPath = window.location.pathname;

    if (newPath !== this.currentPath) {
      this.handlePageLeave();
      this.currentPath = newPath;
      this.handlePageView();
    }
  };

  /**
   * Handles page view events.
   * @returns void
   */
  private handlePageView = (): void => {
    if (this.pageViewCallback) {
      this.pageViewCallback(this.currentPath);
    }
  };

  /**
   * Handles page leave events.
   * @returns void
   */
  private handlePageLeave = (): void => {
    if (this.pageLeaveCallback) {
      this.pageLeaveCallback(this.currentPath);
    }
  };

  /**
   * Handles visibility change events.
   * @returns void
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.handlePageLeave();
    } else {
      this.handlePageView();
    }
  };

  /**
   * Overrides History API methods to detect programmatic navigation.
   * @returns void
   */
  private overrideHistoryMethods = (): void => {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.handleNavigation();
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.handleNavigation();
    };

    this.originalPushState = originalPushState;
    this.originalReplaceState = originalReplaceState;
  };

  /**
   * Restores original History API methods.
   * @returns void
   */
  private restoreHistoryMethods = (): void => {
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }

    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }
  };
}

export { NavigationObserver };
